import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs';
import { AppService } from 'src/app/app.service';

export type SessionPhase = 'inactive' | 'active' | 'warning' | 'expired';

export interface SessionState {
  /** Current lifecycle phase, derived from time remaining before expiry. */
  phase: SessionPhase;
  /** Absolute time the session will expire if not extended. */
  sessionExpiresAt: string | null;
  /** Seconds-before-expiry at which the warning popup appears. */
  warningSeconds: number;
  /** Seconds remaining before the session expires. */
  remainingSeconds: number;
}

/**
 * Cookie-driven session tracker.
 *
 * The backend writes a JS-readable `session_expiry` cookie (epoch ms) on every
 * real authenticated API response, plus a `session_warn` cookie (warning lead
 * time in seconds). This service reads those cookies once per second and
 * derives a live countdown — no polling and no idle tracking.
 *
 * Because any real API call refreshes the cookie, ordinary app activity
 * automatically extends the countdown. When the countdown reaches zero the
 * phase becomes `expired` and the UI shows the "sign back in" popup.
 */
@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private readonly EXPIRY_COOKIE = 'session_expiry';
  private readonly WARN_COOKIE = 'session_warn';
  private readonly LOG_PREFIX = '[SessionDebug][FE]';

  private tickSub: Subscription | null = null;
  private lastPhase: SessionPhase | null = null;
  private tickCount = 0;

  private stateSubject = new BehaviorSubject<SessionState>({
    phase: 'inactive',
    sessionExpiresAt: null,
    warningSeconds: 0,
    remainingSeconds: 0,
  });
  state$: Observable<SessionState> = this.stateSubject.asObservable();

  private logoutSubject = new Subject<void>();
  logout$: Observable<void> = this.logoutSubject.asObservable();

  constructor(private zone: NgZone, private appService: AppService) {}

  private log(event: string, data: Record<string, unknown> = {}): void {
    console.log(this.LOG_PREFIX, event, {
      at: new Date().toISOString(),
      ...data,
    });
  }

  /** Begin the local countdown. Call once the user is authenticated. */
  start(): void {
    this.stop();
    this.log('startt', {
      expiryCookie: this.readCookie(this.EXPIRY_COOKIE),
      warnCookie: this.readCookie(this.WARN_COOKIE),
    });
    this.tick();

    // 1s countdown (outside Angular to avoid change-detection churn).
    this.zone.runOutsideAngular(() => {
      this.tickSub = interval(1000).subscribe(() => {
        this.zone.run(() => this.tick());
      });
    });
  }

  /**
   * Explicitly extend the session ("Stay logged in"). A real API call
   * (GetLoggedInUser) refreshes the backend `session_expiry` cookie; the next
   * tick reads the new value and resets the countdown.
   */
  extend(): void {
    if (!this.tickSub) {
      this.start();
    }
    this.log('extend-clicked', {
      expiryCookie: this.readCookie(this.EXPIRY_COOKIE),
      warnCookie: this.readCookie(this.WARN_COOKIE),
    });
    this.appService.getLoggedInUser().subscribe({
      next: () => {
        this.log('extend-success', {
          expiryCookie: this.readCookie(this.EXPIRY_COOKIE),
          warnCookie: this.readCookie(this.WARN_COOKIE),
        });
        this.tick();
      },
      error: (err) => {
        this.log('extend-error', {
          message: err?.message,
          status: err?.status,
          expiryCookie: this.readCookie(this.EXPIRY_COOKIE),
          warnCookie: this.readCookie(this.WARN_COOKIE),
        });
        this.tick();
      },
    });
  }

  /** Trigger logout / redirect ("Log out now" or "Sign back in"). */
  forceLogout(): void {
    this.log('force-logout', {
      currentPhase: this.stateSubject.value.phase,
      remainingSeconds: this.stateSubject.value.remainingSeconds,
    });
    this.stop();
    this.logoutSubject.next();
  }

  /** Stop the countdown. */
  stop(): void {
    if (this.tickSub) {
      this.log('stop');
    }
    this.tickSub?.unsubscribe();
    this.tickSub = null;
  }

  /** Read a cookie value by name, or null if absent. */
  private readCookie(name: string): string | null {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.substring(name.length + 1)) : null;
  }

  private tick(): void {
    this.tickCount += 1;
    const expiryRaw = this.readCookie(this.EXPIRY_COOKIE);
    const warnRaw = this.readCookie(this.WARN_COOKIE);

    const expiresAt = expiryRaw ? Number(expiryRaw) : NaN;
    const warningSeconds = warnRaw ? Number(warnRaw) : 0;

    if (!expiryRaw || Number.isNaN(expiresAt)) {
      if (this.lastPhase !== 'inactive') {
        this.log('inactive-no-cookie', {
          expiryRaw,
          warnRaw,
          expiresAt,
        });
      }
      // No expiry cookie yet (not authenticated) — stay inactive.
      this.stateSubject.next({
        phase: 'inactive',
        sessionExpiresAt: null,
        warningSeconds: 0,
        remainingSeconds: 0,
      });
      this.lastPhase = 'inactive';
      return;
    }

    const remainingSeconds = Math.max(
      Math.round((expiresAt - Date.now()) / 1000),
      0
    );

    let phase: SessionPhase;
    if (remainingSeconds <= 0) {
      phase = 'expired';
    } else if (remainingSeconds <= warningSeconds) {
      phase = 'warning';
    } else {
      phase = 'active';
    }

    this.stateSubject.next({
      phase,
      sessionExpiresAt: new Date(expiresAt).toISOString(),
      warningSeconds,
      remainingSeconds,
    });

    if (phase !== this.lastPhase || this.tickCount % 30 === 0) {
      this.log('tick', {
        phase,
        previousPhase: this.lastPhase,
        remainingSeconds,
        warningSeconds,
        expiresAt,
        sessionExpiresAt: new Date(expiresAt).toISOString(),
        expiryRaw,
        warnRaw,
      });
    }
    this.lastPhase = phase;

    // Once expired, stop the countdown; the popup drives the redirect.
    if (phase === 'expired') {
      this.log('expired-stop-timer', {
        remainingSeconds,
        warningSeconds,
      });
      this.tickSub?.unsubscribe();
      this.tickSub = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
